import React, { useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const TotalPatients = ({ patientsData }) => {
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [activeWeek, setActiveWeek] = useState(patientsData[0]?.label || "Week 1"); // default to first week

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
    };

    // Get the active week's object
    const weekData = patientsData.find((w) => w.label === activeWeek) || {};

    return (
        <div className="bg-white rounded-xl shadow p-6 flex gap-6">
            {/* Left Summary Section */}
            <div className="w-[30%] space-y-4">
                <h3 className="text-md font-semibold text-[#003A71]">Summary</h3>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "All"
                            ? "border-blue-400 bg-blue-50"
                            : "border-blue-100 bg-white hover:bg-blue-50"
                        }`}
                    onClick={() => handleCategoryClick("All")}
                >
                    <div className="text-sm text-gray-600">All</div>
                    <div className="text-xl font-bold text-[#003A71]">{weekData.followUp}</div>
                </div>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "Recurring"
                            ? "border-blue-400 bg-blue-50"
                            : "border-blue-100 bg-white hover:bg-blue-50"
                        }`}
                    onClick={() => handleCategoryClick("Recurring")}
                >
                    <div className="text-sm text-gray-600">Recurring Patients</div>
                    <div className="text-xl font-bold text-[#003A71]">{weekData.recurring}</div>
                </div>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "New"
                            ? "border-yellow-400 bg-yellow-50"
                            : "border-yellow-100 bg-white hover:bg-yellow-50"
                        }`}
                    onClick={() => handleCategoryClick("New")}
                >
                    <div className="text-sm text-gray-600">New Patients</div>
                    <div className="text-xl font-bold text-[#FBB927]">{weekData.new}</div>
                </div>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "Male"
                            ? "border-red-400 bg-red-50"
                            : "border-red-100 bg-white hover:bg-red-50"
                        }`}
                    onClick={() => handleCategoryClick("Male")}
                >
                    <div className="text-sm text-gray-600">Male Patients</div>
                    <div className="text-xl font-bold text-[#FF6B6B]">{weekData.male}</div>
                </div>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "Female"
                            ? "border-pink-400 bg-pink-50"
                            : "border-pink-100 bg-white hover:bg-pink-50"
                        }`}
                    onClick={() => handleCategoryClick("Female")}
                >
                    <div className="text-sm text-gray-600">Female Patients</div>
                    <div className="text-xl font-bold text-[#FF69B4]">{weekData.female}</div>
                </div>
            </div>

            {/* Right Chart Section */}
            <div className="w-[70%]">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-md font-semibold text-[#003A71]">Total Patients</h3>
                </div>

                {/* Tabs for Weeks */}
                <div className="flex gap-2 mb-4">
                    {patientsData.map((week) => (
                        <button
                            key={week.label}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeWeek === week.label
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-blue-100"
                                }`}
                            onClick={() => setActiveWeek(week.label)}
                        >
                            {week.label}
                        </button>
                    ))}
                </div>

                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={[weekData]} // show only active week’s data
                        margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid vertical={false} stroke="#f0f0f0" />
                        <XAxis
                            dataKey="label"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                        />
                        <Tooltip
                            contentStyle={{ borderRadius: 8, fontSize: 12 }}
                            itemStyle={{ fontSize: 12 }}
                            formatter={(val) => `${val}`}
                        />
                        <Legend
                            verticalAlign="bottom"
                            height={40}
                            wrapperStyle={{
                                fontSize: 12,
                                paddingTop: 12,
                                display: "flex",
                                justifyContent: "center",
                                lineHeight: "20px",
                            }}
                        />

                        {(selectedCategory === "All" || selectedCategory === "Recurring") && (
                            <Bar
                                dataKey="recurring"
                                name="Recurring Patients"
                                fill="#003A71"
                                radius={[4, 4, 0, 0]}
                            />
                        )}
                        {(selectedCategory === "All" || selectedCategory === "New") && (
                            <Bar
                                dataKey="new"
                                name="New Patients"
                                fill="#FBB927"
                                radius={[4, 4, 0, 0]}
                            />
                        )}
                        {(selectedCategory === "All" || selectedCategory === "FollowUp") && (
                            <Bar
                                dataKey="followUp"
                                name="Total Patients"
                                fill="#00C48E"
                                radius={[4, 4, 0, 0]}
                            />
                        )}
                        {(selectedCategory === "All" || selectedCategory === "Male") && (
                            <Bar
                                dataKey="male"
                                name="Male Patients"
                                fill="#FF6B6B"
                                radius={[4, 4, 0, 0]}
                            />
                        )}
                        {(selectedCategory === "All" || selectedCategory === "Female") && (
                            <Bar
                                dataKey="female"
                                name="Female Patients"
                                fill="#FF69B4"
                                radius={[4, 4, 0, 0]}
                            />
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default TotalPatients;
